// The "it needs stairs" pass: one function per finding code, run after every build action.
import { isType, type PropsElement, type Storey } from '../props';
import type { BuildStore, Suggest } from '../types';
import { siteBox, type Box } from './frame';
import { elevationOf, INHABITED_WALLS, orderedStoreys, storeyHeight } from './storeys';

export interface Finding {
  code: string;
  severity: 'info' | 'warn';
  message: string;
  ids: number[];
  storey?: string;
  suggest?: Suggest;
}

// A stair lands a little short of, or past, the floor it serves; treat that as reaching it.
const EPS = 0.15;

export interface CheckOptions {
  // ids the triggering build action just created; scopes unnamed_material to them
  createdIds?: number[];
}

export async function checkBuilding(
  store: BuildStore,
  opts: CheckOptions = {},
): Promise<Finding[]> {
  const storeys = await orderedStoreys(store);
  const elements = await store.elements({ includeSpaces: true });
  const findings: Finding[] = [];
  findings.push(...(await roofBelowStorey(store, storeys, elements)));
  findings.push(...(await noVerticalAccess(store, storeys, elements)));
  findings.push(...storeyWithoutSlab(storeys, elements));
  findings.push(...wallsWithoutOpenings(storeys, elements));
  findings.push(...noSpaces(storeys, elements));
  findings.push(...unnamedMaterial(elements, opts.createdIds));
  return findings;
}

// Storeys worth a room-book check: not the lowest (a foundation stem wall, nothing below it to
// need a slab, openings or access from) and enough walls to be a floor rather than a parapet
// ring (the Duplex's "Roof" carries four wall segments and no rooms).
function habitable(storeys: Storey[], elements: PropsElement[]): Storey[] {
  return storeys.slice(1).filter((s) => wallsOf(elements, s.expressID).length > INHABITED_WALLS);
}

const wallsOf = (elements: PropsElement[], storeyId: number) =>
  elements.filter((e) => e.storeyId === storeyId && isType(e, 'IfcWall'));

const doorsAndWindowsOf = (elements: PropsElement[], storeyId: number) =>
  elements.filter(
    (e) => e.storeyId === storeyId && (isType(e, 'IfcDoor') || isType(e, 'IfcWindow')),
  );

const spacesOf = (elements: PropsElement[], storeyId: number) =>
  elements.filter((e) => e.storeyId === storeyId && isType(e, 'IfcSpace'));

const slabsOf = (elements: PropsElement[], storeyId: number) =>
  elements.filter((e) => e.storeyId === storeyId && isType(e, 'IfcSlab'));

async function noVerticalAccess(
  store: BuildStore,
  storeys: Storey[],
  elements: PropsElement[],
): Promise<Finding[]> {
  const climbers = elements.filter((e) => isType(e, 'IfcStair') || isType(e, 'IfcRamp'));
  const meshes = climbers.length ? await store.mesh(climbers.map((e) => e.expressID)) : [];
  const byId = groupByExpress(meshes);
  const reaches: Box[] = [];
  for (const c of climbers) {
    const box = siteBox(byId.get(c.expressID) ?? []);
    if (box) reaches.push(box);
  }
  const candidates = new Set(habitable(storeys, elements).map((s) => s.expressID));
  const findings: Finding[] = [];
  // index into the full, elevation-sorted list: add_stair builds on the storey below and
  // climbs up to it, so the fix names that storey, not the one missing access.
  for (let i = 1; i < storeys.length; i++) {
    const s = storeys[i]!;
    if (!candidates.has(s.expressID)) continue;
    const elevation = elevationOf(s);
    const reached = reaches.some((b) => b.min[1] - EPS <= elevation && b.max[1] + EPS >= elevation);
    if (reached) continue;
    findings.push({
      code: 'no_vertical_access',
      severity: 'warn',
      message: `${s.name} has no stair or ramp reaching it.`,
      ids: [],
      storey: s.name,
      suggest: { action: 'add_stair', params: { storey: storeys[i - 1]!.name } },
    });
  }
  return findings;
}

// The roof's own geometry: the in-store roof builder's own IfcRoof (it carries a shape, unlike
// the aggregate the original model uses), or an IfcSlab named or storeyed "Roof" (the original
// model's roof geometry; PredefinedType ROOF is not carried into PropsElement).
export const isRoofElement = (e: PropsElement) =>
  isType(e, 'IfcRoof') ||
  (isType(e, 'IfcSlab') && (/roof/i.test(e.storey ?? '') || /roof/i.test(e.name ?? '')));

async function roofBelowStorey(
  store: BuildStore,
  storeys: Storey[],
  elements: PropsElement[],
): Promise<Finding[]> {
  const roofSlabs = elements.filter(isRoofElement);
  const box = roofSlabs.length
    ? siteBox(await store.mesh(roofSlabs.map((e) => e.expressID)))
    : null;
  if (!box) {
    // nothing with a roof's shape is left, which a new storey on the old roof level does
    if (!habitable(storeys, elements).length) return [];
    return [
      {
        code: 'no_roof',
        severity: 'warn',
        message: 'The building has no roof.',
        ids: [],
        suggest: { action: 'add_roof', params: {} },
      },
    ];
  }
  const roofTop = box.max[1];
  const roofStoreyIds = new Set(
    roofSlabs.map((e) => e.storeyId).filter((id): id is number => id != null),
  );
  const offenders = habitable(storeys, elements)
    .filter((s) => !roofStoreyIds.has(s.expressID))
    .filter((s) => elevationOf(s) + storeyHeight(storeys, s) > roofTop + EPS);
  if (!offenders.length) return [];
  return [
    {
      code: 'roof_below_storey',
      severity: 'warn',
      message: `The roof (top at ${round(roofTop)} m) sits below ${offenders.map((s) => s.name).join(', ')}.`,
      ids: roofSlabs.map((e) => e.expressID),
      storey: offenders[0]!.name,
      suggest: { action: 'add_roof', params: { replace: true } },
    },
  ];
}

function storeyWithoutSlab(storeys: Storey[], elements: PropsElement[]): Finding[] {
  const out: Finding[] = [];
  for (const s of habitable(storeys, elements)) {
    if (slabsOf(elements, s.expressID).length) continue;
    out.push({
      code: 'storey_without_slab',
      severity: 'warn',
      message: `${s.name} has walls but no floor slab.`,
      ids: wallsOf(elements, s.expressID).map((e) => e.expressID),
      storey: s.name,
      suggest: { action: 'add_slab', params: { storey: s.name } },
    });
  }
  return out;
}

function wallsWithoutOpenings(storeys: Storey[], elements: PropsElement[]): Finding[] {
  const out: Finding[] = [];
  for (const s of habitable(storeys, elements)) {
    if (doorsAndWindowsOf(elements, s.expressID).length) continue;
    out.push({
      code: 'walls_without_openings',
      severity: 'info',
      message: `${s.name}: no wall carries a door or window.`,
      ids: wallsOf(elements, s.expressID).map((e) => e.expressID),
      storey: s.name,
      // add_opening with nothing but a storey copies the opening the floor below has
      suggest: { action: 'add_opening', params: { storey: s.name, kind: 'door' } },
    });
  }
  return out;
}

function noSpaces(storeys: Storey[], elements: PropsElement[]): Finding[] {
  const out: Finding[] = [];
  for (const s of habitable(storeys, elements)) {
    if (spacesOf(elements, s.expressID).length) continue;
    out.push({
      code: 'no_spaces',
      severity: 'info',
      message: `${s.name} has walls but no rooms.`,
      ids: wallsOf(elements, s.expressID).map((e) => e.expressID),
      storey: s.name,
    });
  }
  return out;
}

function unnamedMaterial(elements: PropsElement[], createdIds?: number[]): Finding[] {
  if (!createdIds?.length) return [];
  const created = new Set(createdIds);
  const bare = elements.filter((e) => created.has(e.expressID) && !e.material);
  if (!bare.length) return [];
  const material = nearestMaterial(elements, bare[0]!);
  return [
    {
      code: 'unnamed_material',
      severity: 'info',
      message: `${bare.length} new elements carry no material.`,
      ids: bare.map((e) => e.expressID),
      ...(material
        ? {
            suggest: {
              action: 'set_material',
              params: { material, ids: bare.map((e) => e.expressID) },
            },
          }
        : {}),
    },
  ];
}

// What the same kind of element is made of elsewhere in the building: the closest one by
// storey, since a new element has no geometry the other rules have already measured.
function nearestMaterial(elements: PropsElement[], target: PropsElement): string | null {
  const sameType = elements.filter(
    (e) => e.expressID !== target.expressID && e.type === target.type && e.material?.names.length,
  );
  const here = sameType.find((e) => e.storeyId === target.storeyId);
  return (here ?? sameType[0])?.material?.names[0] ?? null;
}

function groupByExpress<T extends { expressId: number }>(items: T[]): Map<number, T[]> {
  const out = new Map<number, T[]>();
  for (const item of items) {
    const list = out.get(item.expressId);
    if (list) list.push(item);
    else out.set(item.expressId, [item]);
  }
  return out;
}

const round = (v: number) => Number(v.toFixed(2));
